import type { VolunteerActivityType } from '@/queries/constants'
import composeRichText from '@/client/services/richText/composeRichText'
import plainTextToSerializedRichText from '@/client/services/richText/plainTextToSerializedRichText'
import { faq } from '@/client/services/url'

import type { VolunteerActivityMetadata } from './types'

export const REVIEW_BUSINESS_DAYS = 20

export const VOLUNTEER_ACTIVITIES_METADATA: Record<
  VolunteerActivityType,
  VolunteerActivityMetadata | null
> = {
  'host-session-prep': {
    generalNote: plainTextToSerializedRichText(
      'Always make sure to include 1) full breakdown of session prep activities, 2) time spent per prep activity, 3) specific resources used (including links, e.g. on Google Drive).'
    ),
    okToTrack: [
      {
        key: 'slideshows',
        text: 'Creating/editing slideshows',
        tooltip:
          'If you edited an existing slideshow, please include a list of changes that you made in the description.',
      },
      { key: 'practice-problems', text: 'Creating practice problems' },
      { key: 'interactives', text: 'Creating interactives (Kahoot, Quizlet, etc.)' },
      {
        key: 'practice-assessment',
        text: 'Annotating a practice test your learners will take',
        tooltip:
          'Please make sure to upload an image of your annotations to Google Drive or ' +
          'imgur and include that link.',
      },
      {
        key: 'no-learners',
        text: `If no learners show up to your session, you won't receive tutoring minutes, but you can submit up to 15 minutes of prep time — as long as you had learners registered. You may NOT track time for sessions with no learner signups.`,
      },
    ],
    notOkToTrack: [
      {
        key: 'review',
        text: 'Reviewing a topic for a session',
        tooltip:
          'e.g. "I reviewed the Khan Academy lesson on functions to prep for my session" is not a valid session prep entry.',
      },
      { key: 'tech-setup', text: 'Figuring out tech/Zoom setup' },
      { key: 'without-edits', text: 'The same material without edits for multiple sessions' },
    ],
    maximumMinutes: 45,
    defaultDescription:
      'Spent 25 minutes creating practice problems and examples on domain and range, which I added to a PPT for my Functions series. I spent 10 mins looking up problems online, 10 mins creating my own, and 5 mins adding them to my PPT.',
    linkToFAQ: faq.getArticleUrl('volunteer-hour-session-prep'),
  },

  blog: {
    generalNote: plainTextToSerializedRichText(
      'Please provide a breakdown of the time (e.g. researching, writing, editing), and be sure to include a link to the relevant blog post.'
    ),
    okToTrack: [
      {
        key: 'writing',
        text: 'Writing blog posts',
        tooltip: 'Includes research, interviewing users, etc.',
      },
      {
        key: 'editing',
        text: 'Editing blog posts (for editors and team leads only)',
      },
      {
        key: 'graphics',
        text: 'Making graphics for blog posts (must be approved by team lead to receive hours)',
      },
    ],
    maximumMinutes: 5 * 60,
    defaultDescription:
      'I spent 15 minutes researching, 1.5 hours writing, and 15 minutes editing my blog post titled...',
  },

  certification: {
    generalNote: composeRichText(ctx => [
      ctx.paragraph('Please provide a breakdown of the time you spent on various tasks.'),
      ctx.paragraph(
        ctx.text(
          'Please note: peer reviewing a certification does not count towards hours (it is instead simply a required part of the certification process). This category of volunteer hours is only for people specifically on the Certification Team.',
          { underline: true }
        )
      ),
    ]),
    okToTrack: [
      {
        key: 'queues',
        text: 'Working through certification queues',
        tooltip: 'Includes traditional certifications, honorary certifications, etc.',
      },
      {
        key: 'meetings',
        text: 'Meetings that have a clear outcome that serves Schoolhouse’s mission: team updates, working sessions, coordinating, etc.',
      },
    ],
    notOkToTrack: [
      { key: 'social-meetings', text: 'Meetings that have a primarily social purpose.' },
    ],
    maximumMinutes: 2 * 60,
    defaultDescription:
      'I spent 20 minutes working through traditional video certifications, and 60 minutes working through the honorary certifications queue.',
  },

  mentor: {
    generalNote: plainTextToSerializedRichText(
      'Please provide a breakdown of the time you spent on various tasks. Be specific of what coaching tasks you did, including names of tutors and/or peer reviewers you supported.'
    ),
    okToTrack: [
      {
        key: 'reviewing',
        text: 'Reviewing and evaluating peer review levels',
      },
      {
        key: 'queue',
        text: 'Working through coaching queue',
      },
      {
        key: 'coach',
        text: 'Coach onboarding/training ',
      },
      {
        key: 'collaborating',
        text: 'Collaborate with moderators on subworld-specific initiatives',
      },
    ],
    notOkToTrack: [
      {
        key: 'peer-review',
        text: 'Doing a normal peer review yourself on the website (as that’s already automatically tracked).',
      },
    ],
    maximumMinutes: 2 * 60,
    defaultDescription:
      'This is for the week of September 17th-24th. I reviewed and evaluated the peer review levels of Devki M, William B, and Jadon G, and verified the levels of around 20 peer reviews. I also completed the first half of my coach onboarding.',
  },

  'community-engagement': {
    generalNote: plainTextToSerializedRichText(
      'Please provide a breakdown of the time you spent on various tasks. Be specific about what you personally did and how it contributed to a community event at Schoolhouse. If the time was in an internal meeting, be explicit about what was accomplished at that meeting.'
    ),
    okToTrack: [
      {
        key: 'event-preparing',
        text: 'Preparing for an event (creating materials, recruiting, etc.)',
      },
      {
        key: 'meetings',
        text: 'Meetings that have a clear outcome that serves Schoolhouse’s mission: team updates, working sessions, coordinating, etc.',
      },
      {
        key: 'cet-sessions',
        text: 'Time spent hosting sessions related to CET (assuming it’s not automatically tracked)',
      },
    ],
    notOkToTrack: [
      {
        key: 'social-meetings',
        text: 'Meetings that have a primarily social purpose.',
      },
    ],
    maximumMinutes: 5 * 60,
    defaultDescription:
      'For Nov 27th Schoolhouse Biology Bowl, created slides for event (40 min); created award certificates for three winners (30 min).',
  },

  'policy-and-moderation': {
    generalNote: plainTextToSerializedRichText(
      'Please provide a breakdown of the time you spent on various monitorings and queues. Also, note the number of entries you cleared on the Moderation Queue and the 1:1 Session Recordings Queue, plus how many you flagged'
    ),
    okToTrack: [
      {
        key: 'moderation-queue',
        text: 'Clearing the Moderation Queue',
      },
      {
        key: 'session-queue',
        text: 'Clearing the 1:1 Session Recording Queue',
      },
      {
        key: 'messages',
        text: 'Reviewing Messages',
      },
      {
        key: 'slack',
        text: 'Observing Slack',
      },
    ],
    maximumMinutes: 3 * 60,
    defaultDescription:
      'I spent 20 minutes reviewing 80 entries in the Moderation Queue (I marked 10 of them red, and I flagged 6 of those), I spent 10 minutes reviewing 12 1:1 Session Recordings (I flagged 3 of them), and I spent 14 minutes checking SHW and Slack messages',
  },

  'sat-onboarding': {
    generalNote: composeRichText(ctx => [
      ctx.paragraph(
        'Please provide a breakdown of the time you spent on various tasks. Be specific.'
      ),
      ctx.paragraph(
        ctx.text(
          'Note: This is only for tutors who are SAT Bootcamp Guides. If you are an SAT tutor but not a Guide, please don&apos;t submit.',
          { underline: true }
        )
      ),
    ]),
    okToTrack: [
      {
        key: 'onboarding',
        text: 'Onboarding prep and training',
      },
      {
        key: 'subworlds',
        text: 'Answering questions in subworld',
      },
      {
        key: 'meetings',
        text: 'Meetings that have a clear outcome that serves Schoolhouse’s mission: team updates, working sessions, coordinating, etc.',
      },
    ],
    notOkToTrack: [
      {
        key: 'sessions',
        text: 'Hosting onboarding sessions or office hours (as they’re automatically tracked)',
      },
      {
        key: 'peer-reviews',
        text: 'Completing peer reviews (as they’re automatically tracked)',
      },
      {
        key: 'social-meetings',
        text: 'Meetings that have a primarily social purpose.',
      },
    ],
    maximumMinutes: 2 * 60,
    defaultDescription: `Spent 25 minutes answering tutors' questions (three - Ini, Ella, and Lila - through chat and at least one through the subworld channel).`,
  },

  'social-media': {
    generalNote: plainTextToSerializedRichText(
      'Please provide a breakdown of the time you spent on various tasks. Be specific and mention the particular post you were working on.'
    ),
    okToTrack: [
      {
        key: 'content',
        text: 'Making content (reel, graphic, post, etc.)',
      },
      {
        key: 'meetings',
        text: 'Meetings that have a clear outcome that serves Schoolhouse’s mission: team updates, working sessions, coordinating, etc.',
      },
    ],
    notOkToTrack: [
      {
        key: 'social-meetings',
        text: 'Meetings that have a primarily social purpose.',
      },
    ],
    maximumMinutes: 2 * 60,
    defaultDescription:
      'Announcement on Instagram for upcoming MUN conference: 35 minutes designing, 5 minutes writing, 5 minutes setting up the post on Instagram',
  },

  'tutoring-operations': {
    generalNote: plainTextToSerializedRichText(
      'Please provide a breakdown of the time you spent on various tasks. Be specific, and include dates of volunteer entries that you were reviewing.'
    ),
    okToTrack: [
      {
        key: 'verifying',
        text: 'Verifying volunteer hours',
      },
      {
        key: 'meetings',
        text: 'Meetings that have a clear outcome that serves Schoolhouse’s mission: team updates, working sessions, coordinating, etc.',
      },
    ],
    notOkToTrack: [
      {
        key: 'social-meetings',
        text: 'Meetings that have a primarily social purpose.',
      },
    ],
    maximumMinutes: 5 * 60,
    defaultDescription:
      'Reviewed and verified 15 session prep volunteer hour entries from the week of 11/27 (3) to the week of 12/18 (12).',
  },

  'user-support': {
    generalNote: plainTextToSerializedRichText(
      'Please provide an overview of the user support tickets you helped with, including specifying the week(s) that you answered them. As for time, do your best to keep track. A good rule of thumb is that 5 intercom notification = 1 hour, but be honest if your time deviates from that estimate.'
    ),
    okToTrack: [
      {
        key: 'intercom',
        text: 'Answering Intercom tickets',
      },
      {
        key: 'escalating',
        text: 'Escalating issues to product team',
      },
      {
        key: 'faq',
        text: 'Writing FAQ articles',
      },
      {
        key: 'meetings',
        text: 'Meetings that have a clear outcome that serves Schoolhouse’s mission: team updates, working sessions, coordinating, etc.',
      },
    ],
    notOkToTrack: [
      {
        key: 'social-meetings',
        text: 'Meetings that have a primarily social purpose.',
      },
    ],
    maximumMinutes: 3 * 60,
    defaultDescription:
      'Answered 10 Intercom Chats between 11/27 and 12/18. Escalated several bugs (broken links, assessments not working) and helped answer several parents’ questions about SAT Bootcamps.',
  },

  'welcoming-team': {
    generalNote: plainTextToSerializedRichText(
      'Please be specific in the details of what work you are tracking. For editing slides, describe (generally) the changes that you made to the slide deck. For hosting, please provide accurate session details and why you are tracking manually. For any other tracked activity, be as specific as possible to note why this time should be tracked.'
    ),
    okToTrack: [
      {
        key: 'editing',
        text: 'Editing Welcoming Team slide deck',
      },
      {
        key: 'hosting',
        text: 'Hosting an onboarding session (ONLY IF it isn’t automatically synced and you’ve been given permission from team leads to manually submit)',
      },
      {
        key: 'meetings',
        text: 'Meetings that have a clear outcome that serves Schoolhouse’s mission: team updates, working sessions, coordinating, etc.',
      },
    ],
    notOkToTrack: [
      {
        key: 'social-meetings',
        text: 'Meetings that have a primarily social purpose.',
      },
    ],
    maximumMinutes: 2 * 60,
    defaultDescription:
      'I edited the New Tutor Welcoming Slideshow to prepare for my session. The changes I made were as followed: Customizing the video slide, Adding an Intro Slide, ...',
  },

  other: {
    generalNote: composeRichText(ctx => [
      ctx.paragraph(
        'This category should ',
        ctx.text('almost never', { underline: true }),
        ' be used. If you submit something here, it will probably be rejected unless you received prior approval to submit in this category. Otherwise, you should use the category of the relevant volunteer team.'
      ),
    ]),
    maximumMinutes: 2 * 60,
    defaultDescription: `Here is a detailed description of what I did and the prior approval I received to use this 'Other' category...`,
  },

  moderator: {
    generalNote: composeRichText(ctx => [
      ctx.paragraph(
        `If there's a clear deliverable, facilitation, or approved responsibility—log it. And please share any relevant documentation that you can.`
      ),
    ]),
    okToTrack: [
      {
        key: 'key1',
        text: 'Hosting live events (Community Connects, socials, spotlights, awards, MUNs, EOY party, etc.)',
      },
      {
        key: 'key2',
        text: 'Event prep with outputs (agenda, slides, run-of-show, signup/recap post)',
      },
      {
        key: 'key3',
        text: 'Running New Tutor Welcome / onboarding calls',
      },
      {
        key: 'key4',
        text: 'Facilitating Tutor Learning Circles or pop-up skill clinics',
      },
      {
        key: 'key5',
        text: 'Helping clear peer-review queues during spikes',
      },
      {
        key: 'key6',
        text: 'Drafting/scheduling official program announcements & reminders',
      },
      {
        key: 'key7',
        text: 'Working on approved pilots with PMs (curriculum tweaks, support experiments)',
      },
      {
        key: 'key8',
        text: 'Updating onboarding decks, SOPs, or role playbooks',
      },
      {
        key: 'key9',
        text: 'Curating tutor-created resources into a shared library',
      },
      {
        key: 'key10',
        text: 'Leading scheduled check-ins, office hours, or community shout-outs you facilitate',
      },
    ],
    notOkToTrack: [
      {
        key: 'key11',
        text: 'Passive attendance/lurking in events or Slack you didn’t facilitate',
      },
      {
        key: 'key12',
        text: 'Unapproved 1:1 mentoring or side projects not assigned',
      },
      {
        key: 'key13',
        text: 'Portfolio building, self-promotion, or personal study',
      },
      {
        key: 'key14',
        text: 'Brainstorming with no concrete output (no agenda/doc/event)',
      },
      {
        key: 'key15',
        text: 'Time waiting for no-shows, or general tech/setup beyond normal',
      },
      {
        key: 'key16',
        text: 'Prep time not tied to a specific deliverable or scheduled event',
      },
    ],
    maximumMinutes: 5 * 60,
    defaultDescription:
      'I spent 30 minutes attending a mod sync, 15 minutes drafting a program announcement, and 60 minutes hosting a community game night...',
  },

  'host-session': null,
  'tutor-training': null,
  'live-help': null,
  auditor: null,
  'session-audited': null,
  india: null,
  safety: null,
  'onboarding-buddy': null,
  'coda-hours': null,
  'onboarding-buddy-session': null,
  'attend-new-tutor-onboarding-session': null,
  'attend-sat-bootcamp-onboarding-session': null,
}
